<script lang="ts" setup>
import { onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { notify } from '@kyvg/vue3-notification'

import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { PODPreferences, PodStartScreen, PodFieldPreferences } from '@/types/pod'
import { FieldPreference } from '@/types/general'
import useForm from '@/hooks/use-form'

import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'

const defaultFields: Record<keyof PodFieldPreferences, FieldPreference> = {
    receiver: FieldPreference.Optional,
    consignmentNote: FieldPreference.Optional,
    shipmentNumber: FieldPreference.Optional,
    trailerNumber: FieldPreference.Optional,
    truckNumber: FieldPreference.Optional,
    signature: FieldPreference.Optional,
    images: FieldPreference.Optional,
}

const { t } = useI18n()
const authStore = useAuthStore()
const form = useForm<PODPreferences>({
    dispatches: {
        startScreen: PodStartScreen.details,
        fields: { ...defaultFields },
        haulierHidden: false,
    },
    deliveries: {
        startScreen: PodStartScreen.details,
        fields: { ...defaultFields },
        haulierHidden: false,
    },
    pdfGenerationEmail: null,
})

async function savePODPreferences() {
    try {
        if (!authStore.hasLicense(LicenseType.PODModule)) return

        const response = await form.submit(
            'PUT',
            window.route('pod.company.preferences.update', {
                company: authStore.companyId,
            }),
        )

        if (response) {
            authStore.licenses[LicenseType.PODModule] = {
                ...authStore.licenses[LicenseType.PODModule],
                ...form.data,
            }

            notify({
                title: t('licensePreferencesUpdated'),
                type: 'success',
            })
        }
    } catch (err) {
        console.error('Save error:', err)
        notify({
            title: t('saveFailed'),
            text: t('somethingWentWrong'),
            type: 'error',
        })
    }
}

onBeforeMount(() => {
    const preferences = (authStore.licenses[LicenseType.PODModule] ?? {}) as Partial<PODPreferences>

    form.reset({
        dispatches: {
            startScreen: preferences.dispatches?.startScreen ?? PodStartScreen.details,
            haulierHidden: preferences.dispatches?.haulierHidden ?? false,
            fields: {
                ...defaultFields,
                ...preferences.dispatches?.fields,
            },
        },
        deliveries: {
            startScreen: preferences.deliveries?.startScreen ?? PodStartScreen.details,
            haulierHidden: preferences.deliveries?.haulierHidden ?? false,
            fields: {
                ...defaultFields,
                ...preferences.deliveries?.fields,
            },
        },
        pdfGenerationEmail: preferences.pdfGenerationEmail ?? null,
    })
})
</script>

<template>
    <MyForm :errors="form.errors.value" @submit.prevent="savePODPreferences">
        <div class="flex flex-col lg:flex-row gap-6">
            <div class="space-y-2">
                <h3 class="text-sm font-bold uppercase text-primary-400 dark:text-primary-300">
                    {{ t('dispatches') }}
                </h3>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.startScreen"
                    class="text-center text-sm"
                >
                    <MyRadioButton
                        :label="t('receiving', { property: 'company' })"
                        :value="PodStartScreen.receiver"
                    />
                    <MyRadioButton
                        :label="t('consignmentNote')"
                        :value="PodStartScreen.consignmentNote"
                    />
                    <MyRadioButton
                        :label="t('details')"
                        :value="PodStartScreen.details"
                        class="leading-10"
                    />
                </MyRadioButtonGroup>

                <hr class="my-4" />

                <h3
                    class="text-sm font-bold uppercase tracking-wide text-primary-400 dark:text-primary-300"
                >
                    {{ t('fieldsForDispatch') }}
                </h3>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.receiver"
                    :label="t('receiving', { property: 'company' })"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.consignmentNote"
                    :label="t('consignmentNote')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.shipmentNumber"
                    :label="t('shipmentNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.trailerNumber"
                    :label="t('trailerNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.truckNumber"
                    :label="t('truckNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.signature"
                    :label="t('signature')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.fields.images"
                    :label="t('images')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <hr class="my-4" />

                <MyRadioButtonGroup
                    v-model="form.data.dispatches.haulierHidden"
                    :label="t('showHaulierOnReceipt')"
                >
                    <MyRadioButton :label="t('visible')" :value="false" />
                    <MyRadioButton :label="t('hidden')" :value="true" />
                </MyRadioButtonGroup>
            </div>

            <!-- Right column -->
            <div class="space-y-2">
                <h3 class="text-sm font-bold uppercase text-primary-400 dark:text-primary-300">
                    {{ t('deliveries') }}
                </h3>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.startScreen"
                    class="text-center text-sm"
                >
                    <MyRadioButton
                        :label="t('receiving', { property: 'company' })"
                        :value="PodStartScreen.receiver"
                    />
                    <MyRadioButton
                        :label="t('consignmentNote')"
                        :value="PodStartScreen.consignmentNote"
                    />
                    <MyRadioButton
                        :label="t('details')"
                        :value="PodStartScreen.details"
                        class="leading-10"
                    />
                </MyRadioButtonGroup>

                <hr class="my-4" />

                <h3
                    class="text-sm font-bold uppercase tracking-wide text-primary-400 dark:text-primary-300"
                >
                    {{ t('fieldsForDelivery') }}
                </h3>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.receiver"
                    :label="t('receiving', { property: 'company' })"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.consignmentNote"
                    :label="t('consignmentNote')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.shipmentNumber"
                    :label="t('shipmentNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.trailerNumber"
                    :label="t('trailerNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.truckNumber"
                    :label="t('truckNumber')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.signature"
                    :label="t('signature')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.fields.images"
                    :label="t('images')"
                >
                    <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                    <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                    <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
                </MyRadioButtonGroup>

                <hr class="my-4" />

                <MyRadioButtonGroup
                    v-model="form.data.deliveries.haulierHidden"
                    :label="t('showHaulierOnReceipt')"
                >
                    <MyRadioButton :label="t('shown')" :value="false" />
                    <MyRadioButton :label="t('hidden')" :value="true" />
                </MyRadioButtonGroup>
            </div>
        </div>

        <div v-if="authStore.hasLicense(LicenseType.PodPdfGeneration)" class="mt-6">
            <MyInput
                v-model="form.data.pdfGenerationEmail"
                :label="t('pdfGenerationEmail')"
                :placeholder="t('email')"
                type="email"
                name="pdfGenerationEmail"
            />
        </div>
        <div class="flex justify-end mt-6">
            <MyButton
                scheme="primary"
                :disabled="form.loading.value"
                :loading="form.loading.value"
                type="submit"
            >
                {{ t('save') }}
            </MyButton>
        </div>
    </MyForm>
</template>
